import React, { useEffect, useRef, useState } from 'react';
import {
	filter, merge, Subject, takeUntil, tap,
} from 'rxjs';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';

import { IconChevronLeftSmall, IconChevronRightSmall } from '@sololearnorg/sol';
import { Container } from '../../../../symphony';

import { SlLEContext } from '../../global-constants';
import { SlDataService } from '../../services/sl-data.service';
import progressbarExplosion from '../../animations/progressbar_explosion.json';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { ActionType, RequestType } from '../../global-interface';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';

import s from './ProgressBar.scss';
import { IMaterial } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { SlAnimationLoader } from '../../../../shared/public/SlAnimationLoader/SlAnimationLoader';
import { isLastLesson } from '../../utils/utilsFunctions';

export function ProgressBar(): JSX.Element {
	useStyles(s);
	const [lessonData, setLessonData] = useState<IMaterial>(null);
	const [lessonPageIndex, setLessonPageIndex] = useState<number>(null);
	const [completedSteps, setCompletedSteps] = useState<number>(0);
	const [lastVisitedIndex, setLastVisitedIndex] = useState<number>(0);
	const [celebration, setCelebration] = useState<boolean>(false);
	const [explosion, setExplosion] = useState<boolean>(false);
	const {
		lessonFooterPrmBtn$: primaryButton,
		lessonFooterSecBtn$: secondaryButton,
		lessonFooterContBtn$: continueButton,
	} = useContainerData(SlLEContext, SlDataService, [
		'lessonFooterPrmBtn$',
		'lessonFooterSecBtn$',
		'lessonFooterContBtn$',
	]);

	const { actions$ } = Container.take(SlLEContext, SlActions);

	const sliderRef = useRef(null);
	const explosionRef = useRef(null);

	const { lesson$, lessonPageIndex$, progressBarCelebration$ } = Container.take(SlLEContext, SlDataService);

	useEffect(() => {
		const unsubscriber = new Subject<void>();

		merge(
			lesson$.pipe(
				filter((l) => !!l),
				tap((lesson) => {
					setLessonData(lesson);

					setCompletedSteps(
						lesson.children.filter(
							(el) => el.materialInfo.status?.isCompleted,
						).length,
					);
				}),
			),
			lessonPageIndex$.pipe(tap(setLessonPageIndex)),
			progressBarCelebration$.pipe(tap(setCelebration)),
		)
			.pipe(takeUntil(unsubscriber))
			.subscribe();

		return () => {
			unsubscriber.next();
			unsubscriber.complete();
		};
	}, []);

	useEffect(() => {
		if (lessonPageIndex > completedSteps) {
			setLastVisitedIndex(lessonPageIndex);
		} else if (lastVisitedIndex < completedSteps) {
			setLastVisitedIndex(completedSteps);
		}

		if (celebration) {
			setTimeout(() => {
				const sliderRect: DOMRect = sliderRef.current.getBoundingClientRect();
				const styles: React.CSSProperties = {
					top: `${sliderRect.top - 40}px`,
					left: `${sliderRect.left - 40}px`,
				};
				setExplosion(true);
				Object.assign(explosionRef.current.style, styles);
				progressBarCelebration$.next(false);
				setTimeout(() => setExplosion(false), 1200);
			}, 300);
		}
	}, [lessonPageIndex]);

	const handleRightButtonClick = () => {
		actions$.next({
			type: continueButton ? continueButton.action : primaryButton.action,
			requestType: RequestType.Parallel,
		});
	};

	const onSecondaryButtonClick = () => {
		actions$.next({
			type: secondaryButton.action,
			requestType: RequestType.Parallel,
		});
	};

	const dotPositionIdentifier = () => {
		if (lessonPageIndex === 1) {
			return '24px';
		}

		if (
			lessonPageIndex === lessonData.children.length
			&& lessonData.materialInfo.status?.isCompleted
		) {
			return 'calc(100% - 24px)';
		}

		return `${(100 / (lessonData.children.length - 1)) * (lessonPageIndex - 1)
		}%`;
	};

	const calcPBWidth = () => {
		const progressSteps = lessonData.children.length;

		if (lastVisitedIndex <= 1) {
			return '24px';
		}

		return `${(100 / (progressSteps - 1)) * (lastVisitedIndex - 1)}%`;
	};

	const nextPageDisableState = () => {
		if (continueButton && !isLastLesson()) {
			return false;
		}

		if (
			primaryButton?.disabled
			|| primaryButton?.action !== ActionType.ToNextPage
		) {
			return true;
		}
		if (primaryButton.action === ActionType.ToNextPage) {
			return false;
		}

		return false;
	};

	return lessonData ? (
		<div className="progress-wrapper">
			<IconChevronLeftSmall
				className={classNames('progress-wrapper__navigate-btn', {
					disabled:
						secondaryButton?.disabled
						|| !secondaryButton
						|| secondaryButton?.action !== ActionType.ToPreviousPage,
				})}
				onClick={onSecondaryButtonClick}
				size={24}
			/>
			<div
				className={`progress-bar ${lessonData?.materialInfo.status?.isCompleted
					? 'progress-bar--completed'
					: ''
				}`}
				sl-test-data="cmpProgressBar"
			>
				<div className="progress-bar__container">
					<div
						className={`progress-bar__progress${celebration ? ' celebration' : ''
						}`}
						sl-test-data="elProgressBarProgress"
						style={{ width: calcPBWidth() }}
					>
						<div
							className="progress-bar__slider"
							sl-test-data="elProgressBarSlider"
							style={{ left: dotPositionIdentifier() }}
							ref={sliderRef}
						/>
					</div>
					{explosion && (
						<div
							ref={explosionRef}
							className="progress-bar__explosion"
						>
							<SlAnimationLoader
								animationData={progressbarExplosion}
								show
								autoPlay
								loop
							/>
						</div>
					)}
				</div>
			</div>
			<IconChevronRightSmall
				className={classNames('progress-wrapper__navigate-btn', {
					disabled: nextPageDisableState(),
				})}
				onClick={handleRightButtonClick}
				size={24}
			/>
		</div>
	) : null;
}
